import { useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { bookingApi, billingApi, podsApi, tripsApi, scanningApi, type AssignmentPayload, type ScanEvent, type Stop } from '@/api/client';
import { useAsync } from '@/hooks';
import { useAuth } from '@/auth/AuthContext';
import { Layout } from '@/components/Layout';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { ConfirmModal, Modal } from '@/components/Modal';
import { useToast } from '@/components/Feedback';
import { formatPeso, stopTypeLabel, vehicleRunLabel } from '@/lib/format';
import { AssignmentModal } from '@/components/AssignmentModal';
import { TripRouteMap } from '@/components/TripRouteMap';
import { SignaturePad, type SignaturePadHandle } from '@/components/SignaturePad';
import { internalNavItems } from '@/pages/navItems';
import {
  describeCargoCheck,
  formatManilaLocalDateTime,
  formatManilaDateTime,
  parseManilaLocalDateTime,
} from '@dispatchdash/shared';

const GATE_LABELS: Record<string, string> = {
  pod_signed: 'Proof of delivery signed',
  scans_resolved: 'Scan variances resolved',
  exceptions_decided: 'Exceptions decided',
};

export function TripDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [showCancel, setShowCancel] = useState(false);
  const [showFail, setShowFail] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const toast = useToast();
  const [downloadingPod, setDownloadingPod] = useState<string | null>(null);
  const downloadPod = async (podId: string) => {
    setDownloadingPod(podId);
    try {
      await podsApi.downloadPdf(podId);
    } catch (err) {
      toast((err as { message?: string }).message ?? 'The POD could not be downloaded.', 'error');
    } finally {
      setDownloadingPod(null);
    }
  };
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [assignError, setAssignError] = useState('');
  const [releaseError, setReleaseError] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [failError, setFailError] = useState('');

  const [showClone, setShowClone] = useState(false);
  const [cloneDate, setCloneDate] = useState(formatManilaLocalDateTime(new Date(Date.now() + 24 * 60 * 60 * 1000)));
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState('');

  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState('');
  const [templateSaved, setTemplateSaved] = useState(false);

  const [manualPrice, setManualPrice] = useState('');
  const [manualPriceBasis, setManualPriceBasis] = useState('');
  const [priceSaving, setPriceSaving] = useState(false);
  const [priceError, setPriceError] = useState('');
  const [acceptingQuote, setAcceptingQuote] = useState(false);
  const [assigningPseudo, setAssigningPseudo] = useState(false);
  const [pseudoError, setPseudoError] = useState('');
  const [markReadyError, setMarkReadyError] = useState('');
  const [manifestStopId, setManifestStopId] = useState('');
  const [manifestMsg, setManifestMsg] = useState('');
  const [manifestError, setManifestError] = useState('');
  const [manifestLoading, setManifestLoading] = useState(false);
  const [scanResolvingId, setScanResolvingId] = useState<string | null>(null);
  const [scanReconError, setScanReconError] = useState('');

  const [podStop, setPodStop] = useState<Stop | null>(null);
  const [podSignatory, setPodSignatory] = useState('');
  const [podPieces, setPodPieces] = useState('1');
  const [podSaving, setPodSaving] = useState(false);
  const [podError, setPodError] = useState('');
  const podSigRef = useRef<SignaturePadHandle>(null);

  const { data: trip, loading, error, reload } = useAsync(
    () => tripsApi.get(id!),
    [id],
  );

  const tripRef = trip?.tripNumber ?? trip?.reference ?? 'Trip';

  const gatesRelevant = Boolean(trip && ['completed', 'ready_to_bill'].includes(trip.status));
  const { data: gates, reload: reloadGates } = useAsync(
    () => (gatesRelevant ? billingApi.getGates(id!) : Promise.resolve(null)),
    [id, gatesRelevant],
  );

  const canPreviewInvoice = Boolean(
    trip &&
      hasPermission('billing.run') &&
      ['failed', 'cancelled', 'completed', 'ready_to_bill', 'billed'].includes(trip.status),
  );
  const { data: invoicePreview } = useAsync(
    () => (canPreviewInvoice && id ? billingApi.previewInvoiceAmount(id) : Promise.resolve(null)),
    [id, canPreviewInvoice, trip?.status, trip?.totalCharges],
  );

  const scanReconRelevant = Boolean(
    trip &&
      ['executing', 'on_hold', 'completed', 'ready_to_bill', 'dispatched', 'billed'].includes(trip.status),
  );
  const { data: pendingScans, reload: reloadPendingScans } = useAsync(
    () => (id ? scanningApi.pendingForTrip(id) : Promise.resolve([])),
    [id, trip?.status, gates?.gates?.scans_resolved],
  );
  const hasPendingScans = (pendingScans ?? []).length > 0;
  const showScanRecon =
    scanReconRelevant || hasPendingScans || gates?.gates?.scans_resolved === false;

  const handleClone = async () => {
    setCloning(true);
    setCloneError('');
    try {
      const cloned = await bookingApi.cloneTrip(id!, parseManilaLocalDateTime(cloneDate).toISOString());
      setShowClone(false);
      toast('Trip cloned');
      navigate(`/app/dispatch/trips/${cloned.id}`);
    } catch (err) {
      setCloneError((err as { message?: string }).message ?? 'Failed to clone trip');
    } finally {
      setCloning(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    setTemplateError('');
    try {
      await bookingApi.saveAsTemplate(id!, templateName.trim());
      setTemplateSaved(true);
      setTimeout(() => {
        setShowSaveTemplate(false);
        setTemplateSaved(false);
        setTemplateName('');
      }, 900);
    } catch (err) {
      setTemplateError((err as { message?: string }).message ?? 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSetManualPrice = async () => {
    if (!manualPrice) {
      setPriceError('Enter a price');
      return;
    }
    setPriceSaving(true);
    setPriceError('');
    try {
      await bookingApi.setManualPrice(id!, { manualPrice, manualPriceBasis: manualPriceBasis || undefined });
      setManualPrice('');
      setManualPriceBasis('');
      reload();
    } catch (err) {
      setPriceError((err as { message?: string }).message ?? 'Failed to set price');
    } finally {
      setPriceSaving(false);
    }
  };

  const handleAcceptQuote = async () => {
    setAcceptingQuote(true);
    setPseudoError('');
    try {
      await bookingApi.acceptQuote(id!);
      reload();
    } catch (err) {
      setPseudoError((err as { message?: string }).message ?? 'Failed to accept quote');
    } finally {
      setAcceptingQuote(false);
    }
  };

  const handleAssignPseudoZone = async () => {
    setAssigningPseudo(true);
    setPseudoError('');
    try {
      await bookingApi.assignPseudoZone(id!);
      reload();
    } catch (err) {
      setPseudoError((err as { message?: string }).message ?? 'Failed to mark as outside service area');
    } finally {
      setAssigningPseudo(false);
    }
  };

  const handleManifestFile = async (stopId: string, file: File | null) => {
    if (!file) return;
    setManifestStopId(stopId);
    setManifestLoading(true);
    setManifestError('');
    setManifestMsg('');
    try {
      const csv = await file.text();
      const preview = await scanningApi.previewManifest(stopId, csv);
      const result = await scanningApi.importManifest(stopId, csv);
      setManifestMsg(
        `Imported ${result.imported} barcode(s) for this stop (${preview.uniqueBarcodes} unique in file).`,
      );
    } catch (err) {
      setManifestError((err as { message?: string }).message ?? 'Manifest import failed');
    } finally {
      setManifestLoading(false);
    }
  };

  const handleMarkReady = async () => {
    setBusyAction('markReady');
    setMarkReadyError('');
    try {
      await billingApi.markReady(id!);
      toast('Trip marked ready to bill');
      reload();
      reloadGates();
    } catch (err) {
      setMarkReadyError((err as { message?: string }).message ?? 'Failed to mark ready to bill');
    } finally {
      setBusyAction(null);
    }
  };

  const openPodCapture = (stop: Stop) => {
    setPodStop(stop);
    setPodSignatory('');
    setPodPieces('1');
    setPodError('');
    podSigRef.current?.clear();
  };

  const handleGeneratePod = async () => {
    if (!podStop) return;
    if (!podSignatory.trim()) {
      setPodError('Signatory name is required');
      return;
    }
    if (!podSigRef.current || podSigRef.current.isEmpty()) {
      setPodError('Signature is required');
      return;
    }
    setPodSaving(true);
    setPodError('');
    try {
      await podsApi.generate({
        tripStopId: podStop.id,
        documentType: 'pod',
        signatoryName: podSignatory.trim(),
        piecesDelivered: Math.max(1, Number(podPieces) || 1),
        signatureImageKey: podSigRef.current.toDataURL(),
      });
      setPodStop(null);
      toast('Proof of delivery saved');
      reload();
      reloadGates();
    } catch (err) {
      setPodError((err as { message?: string }).message ?? 'Failed to generate POD');
    } finally {
      setPodSaving(false);
    }
  };

  const handleApprove = async (assignment: AssignmentPayload) => {
    setBusyAction('approve');
    setAssignError('');
    try {
      await tripsApi.approve(id!, assignment);
      setShowAssign(false);
      toast(`${tripRef} assigned and approved`);
      reload();
    } catch (err) {
      setAssignError((err as { message?: string }).message ?? 'Failed to assign & approve');
    } finally {
      setBusyAction(null);
    }
  };

  const handleCancel = async () => {
    setBusyAction('cancel');
    setCancelError('');
    try {
      await tripsApi.cancel(id!);
      setShowCancel(false);
      toast(`${tripRef} cancelled`);
      reload();
    } catch (err) {
      setCancelError((err as { message?: string }).message ?? 'Failed to cancel trip');
    } finally {
      setBusyAction(null);
    }
  };

  const handleFail = async () => {
    setBusyAction('fail');
    setFailError('');
    try {
      await tripsApi.fail(id!);
      setShowFail(false);
      toast(`${tripRef} marked failed`);
      reload();
    } catch (err) {
      setFailError((err as { message?: string }).message ?? 'Failed to mark trip failed');
    } finally {
      setBusyAction(null);
    }
  };

  const handleReleaseDispatch = async () => {
    setBusyAction('dispatch');
    setReleaseError('');
    try {
      await tripsApi.release(id!);
      toast(`${tripRef} dispatched to the crew`);
      reload();
    } catch (err) {
      setReleaseError((err as { message?: string }).message ?? 'Failed to dispatch trip');
    } finally {
      setBusyAction(null);
    }
  };

  const handleApproveRtb = async () => {
    setBusyAction('rtb');
    setReleaseError('');
    try {
      await tripsApi.approveRtb(id!);
      toast('Return-to-base approved');
      reload();
    } catch (err) {
      setReleaseError((err as { message?: string }).message ?? 'Failed to approve return-to-base');
    } finally {
      setBusyAction(null);
    }
  };

  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate('/app/dispatch');
  };

  const handleResolveScan = async (scanId: string, decision: 'accept' | 'reject') => {
    setScanResolvingId(scanId);
    setScanReconError('');
    try {
      await scanningApi.resolve(scanId, decision);
      await reloadPendingScans();
      if (gatesRelevant) await reloadGates();
    } catch (err) {
      setScanReconError((err as { message?: string }).message ?? 'Failed to resolve scan');
    } finally {
      setScanResolvingId(null);
    }
  };

  if (loading) {
    return (
      <Layout navItems={internalNavItems}>
        <div className="loading-center"><div className="spinner" /></div>
      </Layout>
    );
  }

  if (error || !trip) {
    return (
      <Layout navItems={internalNavItems}>
        <div className="page empty-state">
          <h3>{error ? 'Could not load this trip' : 'Trip not found'}</h3>
          <p>{error ?? 'This trip may have been removed.'}</p>
          <div className="row" style={{ justifyContent: 'center' }}>
            {error && <Button type="button" onClick={() => void reload()}>Retry</Button>}
            <Button type="button" variant={error ? 'secondary' : 'primary'} onClick={() => navigate('/app/dispatch')}>Back to board</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout navItems={internalNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <Button type="button" variant="ghost" size="sm" onClick={goBack}>← Back</Button>
            <h1>{trip.reference ?? trip.tripNumber}</h1>
            <div className="row" style={{ marginTop: 'var(--space-sm)' }}>
              <StatusBadge status={trip.status} />
              {trip.timePressure === 'urgent' && <span className="text-urgent" style={{ fontWeight: 600 }}>Urgent</span>}
              {trip.timePressure === 'warning' && <span className="text-warning" style={{ fontWeight: 600 }}>Attention needed</span>}
              {trip.rtbApprovalState === 'pending' && (
                <span className="text-warning" style={{ fontWeight: 600 }}>Return-to-base approval pending</span>
              )}
            </div>
          </div>
          <div className="row">
            {trip.rtbApprovalState === 'pending' && (
              <Button type="button" onClick={handleApproveRtb} loading={busyAction === 'rtb'} disabled={busyAction !== null && busyAction !== 'rtb'}>Approve return-to-base</Button>
            )}
            {trip.status === 'requested' && (
              <Button type="button" onClick={() => { setAssignError(''); setShowAssign(true); }}>Assign & approve</Button>
            )}
            {trip.status === 'confirmed' && hasPermission('trips.dispatch') && (
              <Button type="button" onClick={handleReleaseDispatch} loading={busyAction === 'dispatch'} disabled={busyAction !== null && busyAction !== 'dispatch'} variant="secondary">
                Dispatch
              </Button>
            )}
            {hasPermission('trips.book') && (
              <>
                <Button variant="secondary" onClick={() => setShowClone(true)}>Clone trip</Button>
                <Button variant="secondary" onClick={() => setShowSaveTemplate(true)}>Save as template</Button>
              </>
            )}
            {(['requested', 'confirmed', 'dispatched', 'executing', 'on_hold'].includes(trip.status) &&
              (hasPermission('trips.book') || hasPermission('trips.confirm') || hasPermission('trips.correct'))) && (
              <Button type="button" variant="danger" onClick={() => { setCancelError(''); setShowCancel(true); }}>Cancel trip</Button>
            )}
            {(['dispatched', 'executing', 'on_hold'].includes(trip.status) && hasPermission('trips.correct')) && (
              <Button type="button" variant="danger" onClick={() => { setFailError(''); setShowFail(true); }}>Mark failed</Button>
            )}
          </div>
        </div>

        {releaseError && (
          <p className="form-error" style={{ marginTop: 'var(--space-md)' }}>
            {releaseError}
          </p>
        )}

        <div className="grid-2">
          <section className="detail-section">
            <h3>Trip details</h3>
            <dl className="detail-list">
              <dt>Client</dt><dd>{trip.clientName ?? '—'}</dd>
              <dt>Origin</dt><dd>{trip.origin ?? '—'}</dd>
              <dt>Destination</dt><dd>{trip.destination ?? '—'}</dd>
              <dt>Scheduled</dt><dd>{trip.scheduledDate ? formatManilaDateTime(trip.scheduledDate) : '—'}</dd>
              <dt>Vehicle</dt><dd>{trip.vehiclePlate ?? 'Unassigned'}</dd>
              {trip.vehicleRun && (
                <>
                  <dt>Vehicle run</dt>
                  <dd data-testid="trip-vehicle-run">
                    {vehicleRunLabel(trip.vehicleRun)} · #{trip.vehicleRun.sequence} in queue
                  </dd>
                </>
              )}
              <dt>Driver</dt><dd>{trip.driverName ?? '—'}</dd>
              <dt>Cargo</dt><dd>{trip.cargoSummary ?? '—'}</dd>
              <dt>Charges</dt><dd>{trip.totalCharges ? formatPeso(trip.totalCharges) : '—'}</dd>
              {invoicePreview && (
                <>
                  <dt>Invoice amount</dt>
                  <dd>
                    {formatPeso(invoicePreview.invoiceAmount)}
                    {invoicePreview.exclusiveFailedTrip ? ' (failed-trip charge only)' : ''}
                    {invoicePreview.rawBillableSum !== invoicePreview.invoiceAmount
                      ? ` · before adjustments ${formatPeso(invoicePreview.rawBillableSum)}`
                      : ''}
                  </dd>
                </>
              )}
            </dl>
          </section>

          <section className="detail-section">
            <h3>Stops</h3>
            {trip.stops && trip.stops.length > 0 ? (
              <ol className="stop-list">
                {trip.stops.map((stop: Stop) => (
                  <li key={stop.id} className="stop-item">
                    <div className="stop-seq">{stop.sequence}</div>
                    <div>
                      <div className="stop-type">{stopTypeLabel(stop.type)} · <StatusBadge status={stop.status} size="sm" /></div>
                      <div className="stop-address">{stop.address}</div>
                      {stop.contactName && <div className="text-muted" style={{ fontSize: '0.8125rem' }}>{stop.contactName} {stop.contactPhone}</div>}
                      {stop.cargoCheck && <CargoCheckLine stop={stop} />}
                      {stop.type !== 'return_to_base' && (
                        <div className="text-muted" style={{ fontSize: '0.8125rem', marginTop: 4 }}>
                          {stop.podId ? 'POD on file' : 'No POD'}
                          {stop.podId && (
                            <>
                              {' · '}
                              <button
                                type="button"
                                className="link-button"
                                disabled={downloadingPod === stop.podId}
                                onClick={() => void downloadPod(stop.podId!)}
                              >
                                {downloadingPod === stop.podId ? 'Preparing…' : 'Download POD'}
                              </button>
                            </>
                          )}
                            {!stop.podId &&
                            ['completed', 'ready_to_bill', 'executing', 'on_hold'].includes(trip.status) &&
                            (hasPermission('billing.run') || hasPermission('trips.correct') || hasPermission('trips.view_all')) && (
                              <>
                                {' · '}
                                <button type="button" className="link-button" onClick={() => openPodCapture(stop)}>
                                  Capture POD
                                </button>
                              </>
                            )}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-muted">No stops defined yet.</p>
            )}
          </section>

          <TripRouteMap tripId={trip.id} live={['dispatched', 'executing', 'on_hold'].includes(trip.status)} />

          {(hasPermission('trips.dispatch') || hasPermission('trips.book') || hasPermission('trips.confirm')) &&
            trip.stops &&
            trip.stops.length > 0 && (
            <section className="detail-section">
              <h3>Manifest import</h3>
              <p className="text-muted" style={{ marginBottom: 'var(--space-md)' }}>
                Upload a CSV with columns <code>barcode</code>, optional <code>qty</code> and <code>tags</code> for a stop.
              </p>
              {manifestError && <div className="form-error">{manifestError}</div>}
              {manifestMsg && <p className="text-ok">{manifestMsg}</p>}
              <ul className="manifest-import-list">
                {trip.stops
                  .filter((s) => s.type !== 'return_to_base')
                  .map((stop) => (
                    <li key={stop.id} className="row-between" style={{ marginBottom: 'var(--space-sm)' }}>
                      <span>
                        Stop {stop.sequence}: {stopTypeLabel(stop.type)} — {stop.address}
                      </span>
                      <label className="btn btn-secondary btn-sm">
                        {manifestLoading && manifestStopId === stop.id ? 'Importing…' : 'Import CSV'}
                        <input
                          type="file"
                          accept=".csv,text/csv"
                          hidden
                          disabled={manifestLoading}
                          onChange={(e) => handleManifestFile(stop.id, e.target.files?.[0] ?? null)}
                        />
                      </label>
                    </li>
                  ))}
              </ul>
            </section>
          )}
        </div>

        {trip.quoteRequired && trip.status === 'requested' && (
          <section className="detail-section quote-section">
            <h3>Off-map quote</h3>
            {!trip.pseudoZoneId && (
              <div style={{ marginBottom: 'var(--space-md)' }}>
                <p className="text-warning" style={{ marginBottom: 'var(--space-sm)' }}>
                  One or both addresses are outside our service zones. Mark the trip as outside the service area before this quote can be accepted or the trip confirmed.
                </p>
                {pseudoError && <div className="form-error">{pseudoError}</div>}
                {(hasPermission('trips.book') || hasPermission('trips.confirm')) && (
                  <Button type="button" onClick={handleAssignPseudoZone} loading={assigningPseudo} variant="secondary">
                    Mark as outside service area
                  </Button>
                )}
              </div>
            )}
            {trip.pseudoZoneId && (
              <p className="text-ok" style={{ marginBottom: 'var(--space-md)' }}>
                Marked as outside the service area.
              </p>
            )}
            {!trip.manualPrice ? (
              <>
                <p className="text-muted" style={{ marginBottom: 'var(--space-md)' }}>
                  This trip falls outside standard rate coverage and needs a manually entered price
                  before it can be confirmed.
                </p>
                {hasPermission('trips.book') ? (
                  <div className="quote-form">
                    {priceError && <div className="form-error">{priceError}</div>}
                    <div className="form-row">
                      <Input
                        label="Manual price (₱)"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={manualPrice}
                        onChange={(e) => setManualPrice(e.target.value)}
                        placeholder="0.00"
                      />
                      <Input
                        label="Basis / notes"
                        value={manualPriceBasis}
                        onChange={(e) => setManualPriceBasis(e.target.value)}
                        placeholder="Reason for manual pricing"
                      />
                    </div>
                    <Button type="button" onClick={handleSetManualPrice} loading={priceSaving}>Submit price</Button>
                  </div>
                ) : (
                  <p className="text-warning">Awaiting a dispatcher to enter a manual price.</p>
                )}
              </>
            ) : !trip.quoteAccepted ? (
              <>
                <p style={{ marginBottom: 'var(--space-md)' }}>
                  Proposed price: <strong>{formatPeso(trip.manualPrice)}</strong>
                  {trip.manualPriceBasis ? ` — ${trip.manualPriceBasis}` : ''}
                </p>
                {pseudoError && <div className="form-error">{pseudoError}</div>}
                {hasPermission('trips.confirm') ? (
                  <Button
                    type="button"
                    onClick={handleAcceptQuote}
                    loading={acceptingQuote}
                    disabled={!trip.pseudoZoneId}
                  >
                    Accept quote
                  </Button>
                ) : (
                  <p className="text-warning">Awaiting acceptance of this quote.</p>
                )}
              </>
            ) : (
              <p className="text-ok">Quote accepted — {formatPeso(trip.manualPrice)}{trip.manualPriceBasis ? ` (${trip.manualPriceBasis})` : ''}</p>
            )}
          </section>
        )}

        {trip.quoteRequired && trip.status !== 'requested' && (
          <section className="detail-section quote-section">
            <h3>Off-map quote</h3>
            {trip.quoteAccepted && trip.manualPrice ? (
              <p className="text-ok">Quote accepted — {formatPeso(trip.manualPrice)}{trip.manualPriceBasis ? ` (${trip.manualPriceBasis})` : ''}</p>
            ) : (
              <p className="text-warning">
                Quote workflow incomplete — repricing and acceptance are only allowed while the trip is requested.
              </p>
            )}
          </section>
        )}

        {showScanRecon && (
          <section className="detail-section quote-section">
            <h3>Scan reconciliation</h3>
            <p className="text-muted" style={{ marginBottom: 'var(--space-md)' }}>
              Accept or reject barcodes awaiting confirmation. Unresolved scans block ready-to-bill.
              {' '}
              Also available under Approvals → Scan variances.
            </p>
            {scanReconError && <div className="form-error">{scanReconError}</div>}
            {!hasPendingScans ? (
              <p className="text-ok">No pending scan variances on this trip.</p>
            ) : (
              <ul className="scan-recon-list">
                {(pendingScans ?? []).map((scan: ScanEvent) => (
                  <li key={scan.id} className="row-between scan-recon-item">
                    <div>
                      <div><code>{scan.barcode}</code></div>
                      <div className="text-muted" style={{ fontSize: '0.8125rem' }}>
                        Stop {scan.stopSequence ?? '—'} ({scan.stopType ? stopTypeLabel(scan.stopType) : '—'})
                        {scan.offlineCaptured ? ' · offline' : ''}
                      </div>
                    </div>
                    <div className="row" style={{ gap: 'var(--space-sm)' }}>
                      <Button
                        size="sm"
                        loading={scanResolvingId === scan.id}
                        onClick={() => handleResolveScan(scan.id, 'accept')}
                      >
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        loading={scanResolvingId === scan.id}
                        onClick={() => handleResolveScan(scan.id, 'reject')}
                      >
                        Reject
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {gatesRelevant && gates && (
          <section className="detail-section quote-section">
            <h3>Ready-to-bill gates</h3>
            <ul className="gate-list">
              {Object.entries(gates.gates).map(([key, passed]) => (
                <li key={key} className={passed ? 'gate-pass' : 'gate-fail'}>
                  <span className="gate-icon">{passed ? '✓' : '✗'}</span>
                  {GATE_LABELS[key] ?? key}
                </li>
              ))}
            </ul>
            {gates.allPass ? (
              trip.status === 'completed' && hasPermission('billing.run') && (
                <Button type="button" onClick={handleMarkReady} loading={busyAction === 'markReady'}>Mark ready to bill</Button>
              )
            ) : (
              <p className="text-warning" style={{ fontSize: '0.875rem' }}>
                All gates must pass before this trip can be marked ready to bill.
              </p>
            )}
            {markReadyError && (
              <p className="form-error" style={{ marginTop: 'var(--space-sm)' }}>{markReadyError}</p>
            )}
          </section>
        )}
      </div>

      <ConfirmModal
        open={showCancel}
        onClose={() => setShowCancel(false)}
        onConfirm={handleCancel}
        title={`Cancel ${tripRef}?`}
        message={
          <>
            <p>
              {tripRef}{trip.clientName ? ` for ${trip.clientName}` : ''} will be cancelled and may incur cancellation
              charges depending on how close it is to pickup. This cannot be undone.
            </p>
            {cancelError && <div className="form-error" style={{ marginTop: 'var(--space-md)' }}>{cancelError}</div>}
          </>
        }
        confirmLabel="Cancel trip"
        cancelLabel="Keep trip"
        danger
        loading={busyAction === 'cancel'}
      />

      <ConfirmModal
        open={showFail}
        onClose={() => setShowFail(false)}
        onConfirm={handleFail}
        title="Mark trip failed"
        message={
          <>
            <p>
              Use this for a breakdown, no-show or undeliverable load. {tripRef} will be marked failed and the client
              billed 100% of the base charge. This cannot be undone.
            </p>
            {failError && <div className="form-error" style={{ marginTop: 'var(--space-md)' }}>{failError}</div>}
          </>
        }
        confirmLabel="Mark failed"
        cancelLabel="Keep trip"
        danger
        loading={busyAction === 'fail'}
      />

      <AssignmentModal
        open={showAssign}
        onClose={() => setShowAssign(false)}
        onSubmit={handleApprove}
        loading={busyAction === 'approve'}
        submitError={assignError}
        trip={{
          label: tripRef,
          clientName: trip.clientName,
          vehicleTypeId: trip.vehicleTypeId ?? null,
        }}
      />

      <Modal
        open={showClone}
        onClose={() => setShowClone(false)}
        title="Clone trip"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowClone(false)}>Cancel</Button>
            <Button onClick={handleClone} loading={cloning}>Create clone</Button>
          </>
        }
      >
        <p className="text-muted" style={{ marginBottom: 'var(--space-md)' }}>
          Creates a new booking with the same client, stops and cargo details for a new pickup time.
        </p>
        {cloneError && <div className="form-error">{cloneError}</div>}
        <Input
          label="New scheduled pickup"
          type="datetime-local"
          value={cloneDate}
          onChange={(e) => setCloneDate(e.target.value)}
          required
        />
      </Modal>

      <Modal
        open={showSaveTemplate}
        onClose={() => setShowSaveTemplate(false)}
        title="Save as template"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowSaveTemplate(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} loading={savingTemplate} disabled={!templateName.trim()}>
              Save template
            </Button>
          </>
        }
      >
        {templateSaved ? (
          <p className="text-ok">Template saved.</p>
        ) : (
          <>
            {templateError && <div className="form-error">{templateError}</div>}
            <Input
              label="Template name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g. Weekly Makati → Cebu run"
              required
            />
          </>
        )}
      </Modal>

      <Modal
        open={podStop !== null}
        onClose={() => setPodStop(null)}
        title="Capture POD"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPodStop(null)}>Cancel</Button>
            <Button type="button" onClick={handleGeneratePod} loading={podSaving}>Save POD</Button>
          </>
        }
      >
        {podStop && (
          <>
            <p className="text-muted" style={{ marginBottom: 'var(--space-md)' }}>
              Stop #{podStop.sequence} · {stopTypeLabel(podStop.type)} · {podStop.address}
            </p>
            {podError && <div className="form-error">{podError}</div>}
            <Input
              label="Signatory name"
              value={podSignatory}
              onChange={(e) => setPodSignatory(e.target.value)}
              required
            />
            <Input
              label="Pieces delivered"
              type="number"
              min="1"
              value={podPieces}
              onChange={(e) => setPodPieces(e.target.value)}
            />
            <div className="form-group" style={{ marginTop: 'var(--space-md)' }}>
              <label className="input-label" style={{ display: 'block', marginBottom: 'var(--space-xs)' }}>
                Signature
              </label>
              <SignaturePad ref={podSigRef} />
            </div>
          </>
        )}
      </Modal>

      <style>{`
        .link-button {
          background: none;
          border: none;
          color: var(--color-forest);
          cursor: pointer;
          font-size: inherit;
          font-weight: 600;
          padding: 0;
          text-decoration: underline;
        }
        .detail-section {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
        }
        .detail-section h3 {
          margin-bottom: var(--space-md);
          color: var(--color-forest);
        }
        .detail-list {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: var(--space-sm) var(--space-md);
          font-size: 0.9375rem;
        }
        .detail-list dt {
          font-weight: 500;
          color: var(--color-text-muted);
        }
        .stop-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .stop-item {
          display: flex;
          gap: var(--space-md);
          padding: var(--space-sm) 0;
          border-bottom: 1px solid var(--color-border);
        }
        .stop-item:last-child { border-bottom: none; }
        .stop-seq {
          width: 28px;
          height: 28px;
          background: var(--color-forest);
          color: var(--color-sand);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.8125rem;
          flex-shrink: 0;
        }
        .stop-type { font-weight: 500; font-size: 0.875rem; }
        .stop-address { font-size: 0.8125rem; margin-top: 2px; }
        .quote-section { margin-top: var(--space-lg); }
        .quote-form { max-width: 480px; }
        .form-row {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-md);
        }
        .form-row > * { flex: 1 1 200px; min-width: 0; }
        .form-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          margin-bottom: var(--space-md);
          font-size: 0.875rem;
        }
        .gate-list {
          list-style: none;
          padding: 0;
          margin: 0 0 var(--space-md);
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
          font-size: 0.9375rem;
        }
        .gate-pass { color: var(--color-forest); }
        .gate-fail { color: var(--color-urgent); }
        .gate-icon { display: inline-block; width: 1.25rem; font-weight: 700; }
        .text-ok { color: var(--color-forest); font-weight: 600; }
      `}</style>
    </Layout>
  );
}

/** Cargo check result on a no-manifest stop (piece count / PIC confirmation). */
function CargoCheckLine({ stop }: { stop: Stop }) {
  const c = stop.cargoCheck;
  if (!c) return null;
  const modeLabel = c.mode === 'piece_count' ? 'Piece count' : 'PIC confirmation';
  if (!c.completed) {
    return (
      <div className="text-muted" style={{ fontSize: '0.8125rem', marginTop: 4 }}>
        Cargo check: not yet confirmed ({modeLabel}
        {c.expectedPieces != null ? `, booked ${c.expectedPieces}` : ''})
      </div>
    );
  }
  return (
    <div style={{ fontSize: '0.8125rem', marginTop: 4 }}>
      <span className="text-muted">Cargo check ({modeLabel}): </span>
      {describeCargoCheck({ ...c, stopType: stop.type })}
      {c.confirmedAt && <span className="text-muted"> · {formatManilaDateTime(c.confirmedAt)}</span>}
      {c.mismatch && (
        <>
          {' '}
          <Link
            to={`/app/dispatch/approvals${c.exceptionId ? `?exceptionId=${encodeURIComponent(c.exceptionId)}` : ''}`}
            className="cargo-mismatch-badge"
            title="Count differs from the booking — exception filed for review"
            style={{
              display: 'inline-block',
              padding: '1px 8px',
              borderRadius: 999,
              background: 'rgba(200, 120, 20, 0.15)',
              color: '#9a5b00',
              fontWeight: 700,
              fontSize: '0.75rem',
              textDecoration: 'none',
            }}
          >
            Count mismatch · exception
          </Link>
        </>
      )}
    </div>
  );
}
